/**
 * Pure date / period / index-range math for the performance chart. No Chart.js,
 * DOM, or I/O. Invariant: `labels` is sorted ascending ISO `YYYY-MM-DD`, so
 * string comparison equals date ordering. Malformed inputs are handled
 * defensively rather than thrown — a throw here would blank the whole widget.
 */

/**
 * A selectable display window: a named preset (relative to the most recent
 * label) or an explicit `{ startDate, endDate? }` ISO range.
 */
export type DisplayPeriod =
  | '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'YTD' | 'All'
  | { startDate: string; endDate?: string };

/**
 * Shared period/rolling-window contract for the chart widgets
 * (PerformanceChart, SeriesChart). Kept here with the rest of the pure
 * period math so the two charts can't drift apart.
 */
export type PeriodKey = '1M' | '3M' | '6M' | 'YTD' | '1Y' | '2Y' | '5Y' | 'All' | 'Custom';
export const PRESET_PERIODS: PeriodKey[] = ['1M', '3M', '6M', 'YTD', '1Y', '2Y', '5Y', 'All'];

export type SharpeWindow = '3M' | '6M' | '1Y' | '2Y';
export const SHARPE_WINDOWS: SharpeWindow[] = ['3M', '6M', '1Y', '2Y'];
/** Trading-day window per preset (~21 trading days/month). */
export const SHARPE_WIN_TO_DAYS: Record<SharpeWindow, number> = {
  '3M': 63, '6M': 126, '1Y': 252, '2Y': 504,
};

/** Calendar lookback for each named preset, consumed by {@link subtractCalendar}. */
const LOOKBACK_TABLE: Record<string, { months?: number; years?: number }> = {
  '1M': { months: 1 },
  '3M': { months: 3 },
  '6M': { months: 6 },
  '1Y': { years: 1 },
  '2Y': { years: 2 },
  '5Y': { years: 5 },
};

/** Matches a strict ISO calendar date `YYYY-MM-DD` (no time component). */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Translate a {@link DisplayPeriod} into an inclusive `{ min, max }` index
 * range over `labels`.
 *
 *   - empty `labels`        → `{ min: 0, max: 0 }`
 *   - `{ startDate, endDate? }` → on/after start … on/before end (or last)
 *   - `'All'`               → whole axis
 *   - `'YTD'`               → first label whose year matches the last label's
 *   - `'1M'…'5Y'`           → calendar lookback from the last label
 *   - anything unrecognised → whole axis (safe default)
 */
export function periodToIndexRange(
  period: DisplayPeriod,
  labels: string[],
): { min: number; max: number } {
  const n = (labels && labels.length) || 0;
  if (n === 0) return { min: 0, max: 0 };
  const last = n - 1;

  if (period && typeof period === 'object' && period.startDate) {
    return {
      min: indexOfOrAfter(labels, period.startDate),
      max: period.endDate ? indexOfOrBefore(labels, period.endDate) : last,
    };
  }

  if (period === 'All') return { min: 0, max: last };

  if (period === 'YTD') {
    const year = labels[last]!.slice(0, 4);
    const firstInYear = labels.findIndex((d) => d.slice(0, 4) === year);
    return { min: firstInYear < 0 ? 0 : firstInYear, max: last };
  }

  const calendarLookback = LOOKBACK_TABLE[period as string];
  if (!calendarLookback) return { min: 0, max: last };
  const targetDate = subtractCalendar(labels[last]!, calendarLookback);
  return { min: indexOfOrAfter(labels, targetDate), max: last };
}

/**
 * Cutoff ISO date for a named lookback preset, back from `anchorISO`. Returns
 * `null` for windows with no single lower bound ('All', 'Custom', unrecognised).
 * Single source of the "how far back does <period> reach" mechanic, shared by
 * periodToIndexRange, SeriesChart and FxRateChartPanel so they can't drift.
 */
export function periodCutoffDate(anchorISO: string, period: string): string | null {
  if (period === 'All' || period === 'Custom') return null;
  if (period === 'YTD') return `${anchorISO.slice(0, 4)}-01-01`;
  const lookback = LOOKBACK_TABLE[period];
  if (!lookback) return null;
  return subtractCalendar(anchorISO, lookback);
}

/**
 * Subtract a calendar offset from an ISO date, clamping the day-of-month to the
 * target month's length (so e.g. `2024-03-31` minus 1 month becomes
 * `2024-02-29`). Month underflow rolls the year back.
 *
 * Defensive: a non-ISO `isoDate` is returned unchanged rather than producing a
 * `NaN-NaN-NaN` string that would corrupt the downstream binary search.
 */
export function subtractCalendar(
  isoDate: string,
  { months = 0, years = 0 }: { months?: number; years?: number } = {},
): string {
  if (typeof isoDate !== 'string' || !ISO_DATE_RE.test(isoDate)) return isoDate;
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  let ty = y - years;
  let tm = m - months;
  while (tm <= 0) {
    tm += 12;
    ty -= 1;
  }
  const daysInMonth = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  const td = Math.min(d, daysInMonth);
  return `${ty.toString().padStart(4, '0')}-${tm.toString().padStart(2, '0')}-${td.toString().padStart(2, '0')}`;
}

/**
 * Index of the first label `>= date` (binary search). If every label is before
 * `date` the last index is returned; an empty axis returns `-1`.
 */
export function indexOfOrAfter(labels: string[], date: string): number {
  if (!labels.length) return -1;
  let lo = 0;
  let hi = labels.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (labels[mid]! < date) lo = mid + 1;
    else hi = mid;
  }
  return labels[lo]! >= date ? lo : labels.length - 1;
}

/**
 * Index of the last label `<= date` (binary search). If every label is after
 * `date` index `0` is returned; an empty axis also returns `0`.
 */
export function indexOfOrBefore(labels: string[], date: string): number {
  if (!labels.length) return 0;
  let lo = 0;
  let hi = labels.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (labels[mid]! > date) hi = mid - 1;
    else lo = mid;
  }
  return labels[lo]! <= date ? lo : 0;
}

/**
 * Map a rolling-window length (in trading days) to the smallest history
 * fetch period that comfortably covers it (≈252 trading days per year).
 */
export function periodForTradingDays(tradingDays: number): string {
  if (tradingDays <= 126) return '1Y';
  if (tradingDays <= 252) return '2Y';
  return '5Y';
}

/**
 * Mobile-pan damping multiplier. At or below `baselineRange` visible points the
 * pan is 1:1 (identity); beyond it the multiplier decays exponentially by
 * `decayK` so that wide zoomed-out windows pan more slowly and feel controllable
 * on a touchscreen. Non-finite ranges or a non-positive `decayK` ⇒ identity.
 */
export function mobilePanMultiplier(
  visibleRange: number,
  baselineRange: number,
  decayK: number,
): number {
  if (!Number.isFinite(visibleRange) || visibleRange <= baselineRange) return 1;
  if (!decayK || decayK <= 0) return 1;
  return Math.exp(-decayK * (visibleRange - baselineRange));
}
